import { Box } from "@mui/material";
import {
  MiniDrawer,
  removeAuthToken,
  SayIcon,
} from "@asayinc/component-library";
import {
  Mail,
  MailOutline,
  Business,
  BusinessOutlined,
  HelpOutlineOutlined,
  Dashboard,
  DashboardOutlined,
  People,
  PeopleOutlined,
  AssuredWorkload,
  AssuredWorkloadOutlined,
  Gavel,
  GavelOutlined,
  Summarize,
  SummarizeOutlined,
  HowToVoteOutlined,
  HowToVote,
  DescriptionOutlined,
  SettingsOutlined,
  Settings,
  SendOutlined,
  Send,
} from "@mui/icons-material";
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { SearchPanel } from "src/components/organisms/SearchPanel";
import { useCurrentUserQuery } from "src/store/user";

export function SideNavigationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUserQuery();
  const [isSearchDrawerOpen, setIsSearchDrawerOpen] = useState(false);
  const currentModule = location.pathname.split("/")[1];

  // Check user roles
  const isTabulator =
    currentUser?.tabulators && currentUser.tabulators.length > 0;
  const isClaimsAdministrator =
    currentUser?.claimsAdministrators &&
    currentUser.claimsAdministrators.length > 0;
  const isSolicitor =
    currentUser?.solicitors && currentUser.solicitors.length > 0;

  const handleCloseSearchPanel = () => {
    if (isSearchDrawerOpen) {
      setIsSearchDrawerOpen(false);
    }
  };

  const allItems = [
    {
      value: "search",
      active: isSearchDrawerOpen,
      icon: isSearchDrawerOpen ? (
        <SayIcon name="SearchFill" color="#1968FF" />
      ) : (
        <SayIcon name="SearchOutline" />
      ),
      text: "Search",
    },
    {
      value: "/dashboard",
      active: !isSearchDrawerOpen && currentModule.includes("dashboard"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("dashboard") ? (
          <Dashboard />
        ) : (
          <DashboardOutlined />
        ),
      text: "Dashboard",
    },
    {
      value: "/issuers",
      active: !isSearchDrawerOpen && currentModule.includes("issuers"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("issuers") ? (
          <Business />
        ) : (
          <BusinessOutlined />
        ),
      text: "Issuers",
    },
    {
      value: "/proxy-events/voting",
      active: !isSearchDrawerOpen && currentModule.includes("proxy-events"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("proxy-events") ? (
          <Mail />
        ) : (
          <MailOutline />
        ),
      text: "Proxy Events",
    },
    {
      value: "/additional-mailings",
      active:
        !isSearchDrawerOpen && currentModule.includes("additional-mailings"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("additional-mailings") ? (
          <Send />
        ) : (
          <SendOutlined />
        ),
      text: "Additional Mailings",
    },
    {
      value: "/bankruptcy-events",
      active:
        !isSearchDrawerOpen && currentModule.includes("bankruptcy-events"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("bankruptcy-events") ? (
          <AssuredWorkload />
        ) : (
          <AssuredWorkloadOutlined />
        ),
      text: "Bankruptcies",
    },
    {
      value: "/class-action-events",
      active:
        !isSearchDrawerOpen && currentModule.includes("class-action-events"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("class-action-events") ? (
          <Gavel />
        ) : (
          <GavelOutlined />
        ),
      text: "Class Actions",
    },
    {
      value: "/nobo-events",
      active: !isSearchDrawerOpen && currentModule.includes("nobo-events"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("nobo-events") ? (
          <People />
        ) : (
          <PeopleOutlined />
        ),
      text: "Nobos",
    },
    {
      value: "/reports/share-range-analysis-events",
      active: !isSearchDrawerOpen && currentModule.includes("reports"),
      icon:
        !isSearchDrawerOpen && currentModule.includes("reports") ? (
          <Summarize />
        ) : (
          <SummarizeOutlined />
        ),
      text: "Reports",
    },
    {
      value: "/voting-instructions",
      active: currentModule.includes("voting-instructions"),
      icon: currentModule.includes("voting-instructions") ? (
        <HowToVote />
      ) : (
        <HowToVoteOutlined />
      ),
      text: "Vote Instructions",
    },
  ];

  // Filter items based on user role
  const getFilteredItems = () => {
    // Search and Dashboard are always visible
    const allowedValues: string[] = ["search", "/dashboard"];

    if (isClaimsAdministrator) {
      // Claims administrators can see: bankruptcies, class actions
      allowedValues.push("/bankruptcy-events", "/class-action-events");
    }

    if (isTabulator) {
      // Tabulators can see: proxy events, additional mailings
      allowedValues.push("/proxy-events/voting", "/additional-mailings");
    }

    if (isSolicitor) {
      // Solicitors can see: proxy events, additional mailings, nobos, reports
      allowedValues.push(
        "/proxy-events/voting",
        "/additional-mailings",
        "/nobo-events",
        "/reports/share-range-analysis-events"
      );
    }

    return allItems.filter((item) => allowedValues.includes(item.value));
  };

  const items = getFilteredItems();

  const footerItems = [
    {
      value: "/settings",
      active: currentModule.includes("settings"),
      icon: currentModule.includes("settings") ? (
        <Settings />
      ) : (
        <SettingsOutlined />
      ),
      text: "Settings",
    },
    {
      value: "help",
      active: false,
      icon: <HelpOutlineOutlined />,
      text: "Help",
    },
    {
      value: "fee-schedule",
      active: false,
      icon: <DescriptionOutlined />,
      text: "Fee Schedule",
    },
  ];

  const handleOnNavItemClick = (value: string) => {
    if (value === "search") {
      setIsSearchDrawerOpen(!isSearchDrawerOpen);
    } else if (value === "help") {
      window.open(
        `mailto:support@saytechnologies.com?subject=Intermediary Portal Support`
      );
    } else if (value === "fee-schedule") {
      window.open(`https://www.saytechnologies.com/fee-schedule`);
    } else {
      setIsSearchDrawerOpen(false);
      navigate(value as string);
    }
  };

  // clear both auth tokens and refresh tokens
  const handleLogout = () => {
    removeAuthToken();
    window.localStorage.clear();
    navigate("/login");
  };

  return (
    <>
      <MiniDrawer
        sx={{ height: "100vh", position: "sticky", top: "0" }}
        showSayLogo
        defaultExpanded
        expandedWidth="200px"
        footerItems={footerItems}
        items={items}
        listActiveBg
        profileProps={{
          logoutAction: handleLogout,
          companyLogo: "",
          companyName: "Intermediary Name Goes Here",
          name: `${currentUser?.firstName || ""} ${
            currentUser?.lastName || ""
          }`,
          email: currentUser?.email || "",
          isBrokerPortal: true,
        }}
        onClickItem={handleOnNavItemClick}
      />
      <Box position="relative">
        <SearchPanel
          isOpen={isSearchDrawerOpen}
          onClose={handleCloseSearchPanel}
        />
      </Box>
    </>
  );
}
